import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { SelectedCategoryStateInterface } from "../interfaces/interface";
import {
    useGetAllMenuCategoriesForEditPageQuery,
    useGetCategoriesForModalEditPageQuery,
    useGetMenuCategoriesForEditPageQuery,
    useManageMenuCategoryRelationMutation,
} from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { EditMenuService } from "../service/editMenuService";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { ModalDelete, WithRoleAccess } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { CreateEditModalComponent, RequestInputService, ServiceModalWrapper } from "@me-pos/service-modal";
import i18next from "i18next";
import { useParams } from "react-router-dom";
import { CompanyAccess, MenuCategoryInput, MenuCategoryProxy } from "@me-team/host/main/graphql/types";
import {
    useCreateMenuCategoryMutation,
    useUpdateMenuCategoryMutation,
} from "@me-team/host/main/menu/graphql/menu.hooks";
import CategoryIconPickerWithLabel from "../../Me-menu/components/CategoryIconPicker/CategoryIconPickerWithLabel";
import { useMenuCategoryIcons } from "../../Me-menu/utils/hooks/useMenuCategoryIcons";
import { CategoryPositionType } from "../../../utils/enums";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../Me-menu/utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../../Me-menu/utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../../Me-menu/components/LanguageVersionSelect/TranslationMissingDot";
import { EditMenuRequestInputService } from "../service/editMenuRequestInputService";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import ToastComponent from "@me-team/common/src/components/ui-components/ToastComponent/ToastComponent";
import { useToast } from "@me-team/common/src/hooks/useToast";
import {
    buildPrimaryLanguageVm,
    buildSelectedLanguageVm,
} from "@me-team/common/src/components/Me-menu/SupplementsForDishesPage/utils/languageVm";

interface ExpandedCategoriesState {
    [key: number]: boolean;
}

type CategoryComponentProps = {
    onCategorySelect: (selectedCategory: SelectedCategoryStateInterface) => void;
    selectedCategory: SelectedCategoryStateInterface;
    onMenuStructureChanged: () => Promise<void>;
    canEditMenuContent?: boolean;
};

type DeleteTarget =
    | { type: "category"; categoryId: number }
    | { type: "sub"; categoryId: number; childCategoryId: number };

type CreatedMenuCategory = {
    id: number;
    name?: string | null;
    parentCategory: { id: number } | null;
};

const EditMenuCategoryComponent: React.FC<CategoryComponentProps> = (props) => {
    const basePath = process.env.REACT_APP_POS_URL;
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage, languages } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const primaryLanguageVm = useMemo(
        () => buildPrimaryLanguageVm(primaryLanguage, languages),
        [primaryLanguage, languages]
    );
    const selectedLanguageVm = useMemo(
        () => buildSelectedLanguageVm(selectedLangVersionCode, primaryLanguageVm, languages),
        [selectedLangVersionCode, primaryLanguageVm, languages]
    );
    const selectedLanguageLabel = selectedLanguageVm?.label ?? undefined;
    const [expandedCategories, setExpandedCategories] = useState<ExpandedCategoriesState>({});
    const { t: translate } = useTranslation();
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const translationTooltip = translate("Edit the language version");
    const [editCategoryValues, setEditCategoryValues] = useState<SelectedCategoryStateInterface | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
    const [currentCategory, setCurrentCategory] = useState<CreatedMenuCategory | null>(null);
    const { id, branchId } = useParams();
    const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
    const canEditMenuContentByRole = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canEditMenuContent = props.canEditMenuContent ?? canEditMenuContentByRole;
    const canCreateDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryCreate);
    const canEditDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryEdit);

    const toggleExpandCategory = (categoryId: number) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    const {
        data: categoriesData,
        loading: loadingMenuCategories,
        refetch: refetchCategories,
    } = useGetMenuCategoriesForEditPageQuery({
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            menuId: Number(id),
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "global",
        },
    });

    const {
        data: categoriesNotInMenuData,
        loading: loadingCategoriesNotInMenu,
        refetch: categoriesNotInMenuDataRefetch,
    } = useGetCategoriesForModalEditPageQuery({
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            menuId: Number(id),
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "global",
        },
    });

    const categoryServiceWithTranslationMode = useMemo(
        () => ({
            ...EditMenuService,
            categoryModuleCreator: (
                categoryList: MenuCategoryProxy[],
                categoryId: number | null,
                subcategoryId: number | null
            ) => EditMenuService.categoryModuleCreator(categoryList, categoryId, subcategoryId, isTranslationMode),
        }),
        [isTranslationMode]
    );

    const { data: allCategoriesData } = useGetAllMenuCategoriesForEditPageQuery({
        skip: !editCategoryValues || !selectedLangVersionCode,
        variables: {
            companyId: Number(currentCompanyId),
            locale: selectedLangVersionCode,
        },
    });

    useEffect(() => {
        const list = categoriesData?.user.company.branches[0].menusList[0].categories ?? [];
        if (list.length === 0) return;

        if (!props.selectedCategory.categoryId && !props.selectedCategory.childCategoryId) {
            props.onCategorySelect({
                categoryId: list[0].id,
                childCategoryId: null,
                categoryName: list[0].name,
                categoryNameOrigin: list[0].nameOrigin,
            });
        }
        // eslint-disable-next-line
    }, [categoriesData, props.selectedCategory.categoryId, props.selectedCategory.childCategoryId]);

    const [manageMenuCategoryRelation] = useManageMenuCategoryRelationMutation({
        context: {
            errorType: "local",
        },
    });

    const [updateMenuCategory] = useUpdateMenuCategoryMutation({
        context: {
            errorType: "local",
        },
    });

    const [createMenuCategory] = useCreateMenuCategoryMutation({
        context: {
            errorType: "local",
        },
    });

    const categoryIcons = useMenuCategoryIcons(currentCompanyId);

    const initialCategoryList = useMemo(() => {
        const list = categoriesData?.user.company.branches[0].menusList[0].categories ?? [];
        return EditMenuService.categoryBaseInit(list);
    }, [categoriesData]);

    const normalizedAllCategoriesForModal = useMemo(() => {
        const list = allCategoriesData?.user.company.meMenuCategories ?? [];

        return list.map((category) => ({
            ...category,
            name: EditMenuService.resolveTranslatedName(category.name, category.nameOrigin),
            comment: EditMenuService.resolveTranslatedName(category.comment, category.descriptionOrigin),
            childCategories: (category.childCategories ?? []).map((childCategory) => ({
                ...childCategory,
                name: EditMenuService.resolveTranslatedName(childCategory.name, childCategory.nameOrigin),
                comment: EditMenuService.resolveTranslatedName(childCategory.comment, childCategory.descriptionOrigin),
            })),
        }));
    }, [allCategoriesData]);

    const viewCategoryList = useMemo(() => {
        return EditMenuService.applyActive(initialCategoryList, props.selectedCategory);
    }, [initialCategoryList, props.selectedCategory]);

    const handleModalDeleteClose = () => setDeleteTarget(null);

    const handleRowClick = (e: React.MouseEvent<HTMLElement>, onSelect: () => void) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-action="true"]')) return;
        if (target.closest('button:disabled, [aria-disabled="true"]')) return;
        onSelect();
    };

    const deleteBody = useMemo(() => {
        if (!deleteTarget) return "";

        const cat = viewCategoryList.find((category) => category.id === deleteTarget.categoryId);
        if (!cat) return "";

        if (deleteTarget.type === "category") {
            const catNameVm = buildTranslationTextVm({
                translatedValue: cat.name,
                originValue: cat.nameOrigin,
                isTranslationMode,
            });
            const hasSubcats = cat.childCategoriesList.length > 0;
            const bodyText = hasSubcats
                ? translate("deleteCategoryWithSubCatInMenuConfirm", { name: catNameVm.displayText })
                : translate("deleteCategoryInMenuConfirm", { name: catNameVm.displayText });

            return (
                <span className="d-inline-flex align-items-center">
                    <span className="me-1">{bodyText}</span>
                    {catNameVm.isMissingTranslation ? <TranslationMissingDot tooltipText={translationTooltip} /> : null}
                </span>
            );
        }

        const sub = cat.childCategoriesList.find((subCat) => subCat.id === deleteTarget.childCategoryId);
        const subNameVm = buildTranslationTextVm({
            translatedValue: sub?.name,
            originValue: sub?.nameOrigin,
            isTranslationMode,
        });
        const bodyText = translate("deleteCategoryInMenuConfirm", { name: subNameVm.displayText });

        return (
            <span className="d-inline-flex align-items-center">
                <span className="me-1">{bodyText}</span>
                {subNameVm.isMissingTranslation ? <TranslationMissingDot tooltipText={translationTooltip} /> : null}
            </span>
        );
    }, [deleteTarget, viewCategoryList, translate, isTranslationMode, translationTooltip]);

    const onAddCategoryToList = async (currentCategory: MenuCategoryProxy) => {
        if (!canEditMenuContent) return;

        await manageMenuCategoryRelation({
            variables: {
                companyId: Number(currentCompanyId),
                branchId: Number(branchId),
                menuId: Number(id),
                input: {
                    category: currentCategory.id,
                },
            },
            context: {
                errorType: "global",
            },
        });
        await refetchCategories();
        setIsAddCategoryModalOpen(false);
    };

    const onCategoryItemMove = async (
        currentCategoryId: number,
        positionType: string,
        index: number,
        isChildCat: boolean
    ) => {
        if (!canEditMenuContent) return;

        const currentIndex: number = index + 1;
        const positionIndex: number = positionType === CategoryPositionType.UP ? currentIndex - 1 : currentIndex + 1;

        if (isChildCat) {
            await manageMenuCategoryRelation({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    menuId: Number(id),
                    input: {
                        category: currentCategoryId,
                        childIndex: positionIndex,
                    },
                },
            });
        } else {
            await manageMenuCategoryRelation({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    menuId: Number(id),
                    input: {
                        category: currentCategoryId,
                        index: positionIndex,
                    },
                },
            });
        }

        await refetchCategories();
    };

    const onCreateCategoryHandler = async (variables: MenuCategoryInput) => {
        if (!canCreateDishCategory) return;

        const parentCategoryId =
            variables.parentCategoryId && variables.parentCategoryId > 0 ? variables.parentCategoryId : null;

        const input: MenuCategoryInput = {
            name: variables.name,
            deleted: false,
            comment: variables.comment,
            parentCategoryId,
        };
        if ("faviconId" in variables) {
            input.faviconId = variables.faviconId ?? null;
        }

        await createMenuCategory({
            variables: {
                companyId: Number(currentCompanyId),
                branchId: Number(branchId),
                input,
                locale: primaryLanguageIso,
            },
            onCompleted: (data) => {
                setCurrentCategory({
                    ...data.createMenuCategory,
                    parentCategory: parentCategoryId ? { id: parentCategoryId } : null,
                });
            },
        });
    };

    if (loadingMenuCategories || !categoriesData) return <Spinner />;

    return (
        <>
            <Row className="justify-content-between align-items-center mb-4">
                <Col sm={6} className="mb-3 mb-sm-0">
                    <h3 className="fs-24 mb-0">{translate("Category")}</h3>
                </Col>
                <Col sm={6} md={5} lg={4} xl={6} className="text-end">
                    {canEditMenuContent && (
                        <Button
                            className="custom-btn p-1 text-truncate"
                            variant="primary"
                            onClick={() => {
                                if (!canEditMenuContent) return;
                                setIsAddCategoryModalOpen(true);
                            }}
                        >
                            <i className="bi bi-plus-lg me-2 fs-16 lh-sm"></i>
                            <span className="fs-7 fw-bold">{translate("Add Category")}</span>
                        </Button>
                    )}
                </Col>
            </Row>
            {viewCategoryList.length === 0 ? (
                <div
                    className="d-flex align-items-center justify-content-center border border-additional-grey h-100 rounded w-100"
                    style={{ minHeight: "120px" }}
                >
                    <p className={"mb-0 text-grey"}>{translate("Add categories to the menu")}</p>
                </div>
            ) : (
                viewCategoryList.map((category, index) => {
                    const categoryNameVm = buildTranslationTextVm({
                        translatedValue: category.name,
                        originValue: category.nameOrigin,
                        isTranslationMode,
                    });

                    return (
                        <React.Fragment key={`category-${category.id}`}>
                            <div
                                className={`py-3 ${
                                    index !== viewCategoryList.length - 1 ? "border-bottom" : "border-bottom-0"
                                }`}
                            >
                                <div
                                    className={`btn-category w-100 d-flex justify-content-between align-items-center p-6 rounded-1 
                                ${category.isActive ? "bg-additional-grey" : ""}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) =>
                                        handleRowClick(e, () =>
                                            props.onCategorySelect({
                                                categoryId: category.id,
                                                childCategoryId: null,
                                                categoryName: category.name,
                                                categoryNameOrigin: category.nameOrigin,
                                            })
                                        )
                                    }
                                >
                                    <div className="d-flex align-items-center">
                                        {category.childCategories.length !== 0 ? (
                                            <button
                                                type="button"
                                                className="btn p-1 me-2"
                                                data-action="true"
                                                onClick={() => toggleExpandCategory(category.id)}
                                            >
                                                {expandedCategories[category.id] ? (
                                                    <i className="bi bi-caret-down-fill" />
                                                ) : (
                                                    <i className="bi bi-caret-right-fill" />
                                                )}
                                            </button>
                                        ) : (
                                            <span className="me-2" />
                                        )}

                                        {category?.favicon ? (
                                            <img
                                                className="me-2"
                                                src={`${basePath}/${category.favicon.path}`}
                                                alt={categoryNameVm.displayText}
                                                width={20}
                                                height={20}
                                            />
                                        ) : null}

                                        <p className="fs-16 mb-0 text-break">{categoryNameVm.displayText}</p>
                                        {categoryNameVm.isMissingTranslation ? (
                                            <TranslationMissingDot tooltipText={translationTooltip} />
                                        ) : null}
                                    </div>

                                    <div className="d-flex align-items-center ms-3" data-action="true">
                                        <WithRoleAccess permission={CompanyAccess.MenuDishCategoryEdit}>
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="text-dark p-0 me-3"
                                                onClick={() => {
                                                    if (!canEditDishCategory) return;
                                                    setEditCategoryValues({
                                                        categoryId: category.id,
                                                        childCategoryId: null,
                                                        categoryName: category.name,
                                                        categoryNameOrigin: category.nameOrigin,
                                                    });
                                                }}
                                            >
                                                <i className="bi bi-pencil text-dark" />
                                            </Button>
                                        </WithRoleAccess>

                                        {canEditMenuContent && (
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="text-dark p-0"
                                                onClick={() => {
                                                    if (!canEditMenuContent) return;
                                                    setDeleteTarget({
                                                        type: "category",
                                                        categoryId: category.id,
                                                    });
                                                }}
                                            >
                                                <i className="bi bi-trash text-dark" />
                                            </Button>
                                        )}

                                        <div className="d-flex border-start border-secondary ps-3 ms-3">
                                            <button
                                                type="button"
                                                className="btn btn-icon p-0 me-3"
                                                disabled={index === 0 || !canEditMenuContent}
                                                onClick={async () => {
                                                    await onCategoryItemMove(
                                                        category.id,
                                                        CategoryPositionType.UP,
                                                        index,
                                                        false
                                                    );
                                                }}
                                            >
                                                <i className="bi bi-chevron-up fs-16" />
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-icon p-0"
                                                disabled={index === viewCategoryList.length - 1 || !canEditMenuContent}
                                                onClick={async () => {
                                                    await onCategoryItemMove(
                                                        category.id,
                                                        CategoryPositionType.DOWN,
                                                        index,
                                                        false
                                                    );
                                                }}
                                            >
                                                <i className="bi bi-chevron-down fs-16" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {expandedCategories[category.id] &&
                                category.childCategoriesList.length > 0 &&
                                category.childCategoriesList.map((sub, childIndex) => {
                                    const subNameVm = buildTranslationTextVm({
                                        translatedValue: sub.name,
                                        originValue: sub.nameOrigin,
                                        isTranslationMode,
                                    });

                                    return (
                                        <div
                                            key={`child-category-${sub.id}`}
                                            className={`ms-3 py-2 ${
                                                childIndex !== category.childCategoriesList.length - 1
                                                    ? "border-bottom"
                                                    : "border-bottom-0"
                                            }`}
                                        >
                                            <div
                                                className={`btn-category w-100 d-flex justify-content-between align-items-center p-6 rounded-1
                                            ${sub.isActive ? "bg-additional-grey" : ""}
                                        `}
                                                role="button"
                                                tabIndex={0}
                                                onClick={(e) =>
                                                    handleRowClick(e, () =>
                                                        props.onCategorySelect({
                                                            categoryId: category.id,
                                                            childCategoryId: sub.id,
                                                            categoryName: sub.name,
                                                            categoryNameOrigin: sub.nameOrigin,
                                                        })
                                                    )
                                                }
                                            >
                                                <div className="d-flex align-items-center">
                                                    <p className="fs-16 mb-0 text-break">{subNameVm.displayText}</p>
                                                    {subNameVm.isMissingTranslation ? (
                                                        <TranslationMissingDot tooltipText={translationTooltip} />
                                                    ) : null}
                                                </div>

                                                <div className="d-flex align-items-center ms-3" data-action="true">
                                                    <WithRoleAccess permission={CompanyAccess.MenuDishCategoryEdit}>
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="text-dark p-0 me-3"
                                                            onClick={() => {
                                                                if (!canEditDishCategory) return;
                                                                setEditCategoryValues({
                                                                    categoryId: category.id,
                                                                    childCategoryId: sub.id,
                                                                    categoryName: sub.name,
                                                                    categoryNameOrigin: sub.nameOrigin,
                                                                });
                                                            }}
                                                        >
                                                            <i className="bi bi-pencil text-dark" />
                                                        </Button>
                                                    </WithRoleAccess>

                                                    {canEditMenuContent && (
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="text-dark p-0"
                                                            onClick={() => {
                                                                if (!canEditMenuContent) return;
                                                                setDeleteTarget({
                                                                    type: "sub",
                                                                    categoryId: category.id,
                                                                    childCategoryId: sub.id,
                                                                });
                                                            }}
                                                        >
                                                            <i className="bi bi-trash text-dark" />
                                                        </Button>
                                                    )}

                                                    <div className="d-flex border-start border-secondary ps-3 ms-3">
                                                        <button
                                                            type="button"
                                                            className="btn btn-icon p-0 me-3"
                                                            disabled={childIndex === 0 || !canEditMenuContent}
                                                            onClick={async () => {
                                                                await onCategoryItemMove(
                                                                    sub.id,
                                                                    CategoryPositionType.UP,
                                                                    childIndex,
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <i className="bi bi-chevron-up fs-16" />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-icon p-0"
                                                            disabled={
                                                                childIndex ===
                                                                    category.childCategoriesList.length - 1 ||
                                                                !canEditMenuContent
                                                            }
                                                            onClick={async () => {
                                                                await onCategoryItemMove(
                                                                    sub.id,
                                                                    CategoryPositionType.DOWN,
                                                                    childIndex,
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <i className="bi bi-chevron-down fs-16" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </React.Fragment>
                    );
                })
            )}
            {deleteTarget && (
                <ModalDelete
                    show
                    handleClose={handleModalDeleteClose}
                    title={translate("Delete category")}
                    body={deleteBody}
                    buttons={[
                        {
                            text: translate("cancel"),
                            onClick: handleModalDeleteClose,
                            variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                            className: "d-flex justify-content-center align-items-center w-100",
                        },
                        {
                            text: translate("delete"),
                            onClick: async () => {
                                if (!canEditMenuContent) return;

                                const currentCategoryToDelete: number =
                                    deleteTarget.type === "category"
                                        ? deleteTarget.categoryId
                                        : deleteTarget.childCategoryId;

                                await manageMenuCategoryRelation({
                                    variables: {
                                        companyId: Number(currentCompanyId),
                                        branchId: Number(branchId),
                                        menuId: Number(id),
                                        input: {
                                            category: currentCategoryToDelete,
                                            deleted: true,
                                        },
                                    },
                                });
                                await refetchCategories();
                                if (deleteTarget.categoryId === props.selectedCategory.categoryId) {
                                    props.onCategorySelect({
                                        categoryId: null,
                                        childCategoryId: null,
                                        categoryName: "",
                                    });
                                }
                                await props.onMenuStructureChanged();
                                handleModalDeleteClose();
                            },
                            variant: "primary",
                            className: "w-100 fw-bold",
                        },
                    ]}
                />
            )}
            {editCategoryValues && allCategoriesData && (
                <CreateEditModalComponent
                    i18n={i18next}
                    categoryId={editCategoryValues.categoryId}
                    subcategoryId={editCategoryValues.childCategoryId}
                    isOpen={!!editCategoryValues}
                    categoryInput={EditMenuRequestInputService.createServiceCategoryInput(
                        editCategoryValues.categoryId,
                        editCategoryValues.childCategoryId,
                        allCategoriesData.user.company.meMenuCategories
                    )}
                    categoryList={normalizedAllCategoriesForModal}
                    isCreateAction={false}
                    onClose={() => {
                        setEditCategoryValues(null);
                    }}
                    onSubmit={() => {}}
                    onSubmitCreate={async () => {}}
                    onSubmitUpdate={async (variables) => {
                        if (!canEditDishCategory) return;

                        await updateMenuCategory({
                            variables: {
                                companyId: Number(currentCompanyId),
                                branchId: Number(branchId),
                                id: variables.id,
                                input: variables.input,
                                locale: selectedLangVersionCode,
                            },
                        });
                        await refetchCategories();
                    }}
                    onCopyOrigin={() => toggleShowToast(translate("Original text copied"))}
                    requestInputService={EditMenuRequestInputService}
                    translationFallback={{
                        enabled: true,
                        isTranslationMode,
                        showOriginAsPlaceholderWhenMissing: true,
                        languageVersionLabel: selectedLanguageLabel,
                        showLockedFieldsInfoBanner: isTranslationMode,
                        lockedFieldsInfoText: translate(
                            "To edit locked fields, switch to the primary language version in the Language version selector."
                        ),
                    }}
                    currentCategory={null}
                    categoryService={EditMenuService}
                    renderCategoryIconPicker={({ value, onChange, disabled }) => (
                        <CategoryIconPickerWithLabel
                            icons={categoryIcons}
                            value={value}
                            onChange={onChange}
                            disabled={disabled || isTranslationMode}
                            basePath={basePath}
                        />
                    )}
                />
            )}
            {isAddCategoryModalOpen && categoriesNotInMenuData && !loadingCategoriesNotInMenu && (
                <ServiceModalWrapper
                    companyId={Number(currentCompanyId)}
                    branchId={Number(branchId)}
                    i18n={i18next}
                    selectedCategoryId={null}
                    selectedSubcategoryId={null}
                    apolloUrl={process.env.REACT_APP_API_URL}
                    isOpen={isAddCategoryModalOpen}
                    translationFallback={{
                        languageVersionLabel: primaryLanguageVm?.label,
                    }}
                    onClose={() => {
                        setIsAddCategoryModalOpen(false);
                    }}
                    onCategorySelect={async (selectedCategory) => {
                        await onAddCategoryToList(selectedCategory);
                        await props.onMenuStructureChanged();
                    }}
                    onSubcategorySelect={async (selectedSubcategory) => {
                        await onAddCategoryToList(selectedSubcategory);
                        await props.onMenuStructureChanged();
                    }}
                    categoryList={EditMenuService.categoryModuleCreator(
                        categoriesNotInMenuData.user.company.branches[0].menusList[0].categoriesNotInMenu,
                        null,
                        null,
                        isTranslationMode
                    )}
                    showMissingTranslationDot={isTranslationMode}
                    renderMissingTranslationDot={() => <TranslationMissingDot tooltipText={translationTooltip} />}
                    categoryInput={RequestInputService.createServiceCategoryInput(
                        null,
                        null,
                        categoriesNotInMenuData.user.company.branches[0].menusList[0].categoriesNotInMenu
                    )}
                    categoryListData={categoriesNotInMenuData.user.company.branches[0].menusList[0].categoriesNotInMenu}
                    refetchCategories={categoriesNotInMenuDataRefetch}
                    onSubmitCreate={async (variables: MenuCategoryInput) => {
                        await onCreateCategoryHandler(variables);
                    }}
                    canCreateCategory={canCreateDishCategory}
                    currentCategory={currentCategory}
                    categoryService={categoryServiceWithTranslationMode}
                    requestInputService={RequestInputService}
                    normalizeCategoryData={(data) => {
                        return data.user.company.branches[0].menusList[0].categoriesNotInMenu || [];
                    }}
                    renderCategoryIconPicker={({ value, onChange, disabled }) => (
                        <CategoryIconPickerWithLabel
                            icons={categoryIcons}
                            value={value}
                            onChange={onChange}
                            disabled={disabled}
                            basePath={basePath}
                        />
                    )}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default React.memo(EditMenuCategoryComponent);
